import {
  useGetFormDetail2,
  useGetFormalityRequestDetail,
  useUpdateRevision,
  useGetRevisions,
  useLeaderApproval,
  type Correction,
  type Comment,
  type DeniedCorrection,
} from "@/queries/formaly-request";
import type { FormDetailResponse } from "@/types/entities/formality-request";
import {
  Button,
  Heading,
  Icon,
  TableData,
  useToast,
  Badge,
  Textarea,
} from "@elaniintc/goes-design-system";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useNavigate, useParams } from "react-router";
import { tableColumns, type Fields } from "./form-correction-table";
import { useInfo } from "../../hooks/use-info";
import { useCallback, useEffect, useMemo, useState } from "react";
import DenyReasonModal from "./deny-reason-modal";
import { cn } from "@/utils/theme";
import { Skeleton } from "@/components/ui/skeleton";
import { getInternalDeadlineFromFormData } from "@/utils/procedures-ui";
import { DenyGeneralReasonModal } from "../deny-general-modal";
import { RevisionSelector, type PendingVoBoMap } from "./revision-selector";
import { useGetMe } from "@/queries/me";
import { getCorrections } from "../utils/utils";
import { parseAsString, useQueryState } from "nuqs";
import type { AxiosError } from "axios";
import FieldHistoryModal from "./field-history-modal";

/**
 * Limpia las etiquetas HTML de un texto y devuelve solo el contenido de texto plano
 * @param html - String que puede contener HTML
 * @returns String sin etiquetas HTML
 */
const stripHtmlTags = (html: string | null | undefined): string => {
  if (!html || typeof html !== "string") {
    return "";
  }
  return html.replace(/<[^>]*>/g, "").trim();
};

const SectionList = ({
  stepTitle,
  data,
  corrections,
  setCorrections,
  currentGroupId,
  lockedApprovedFields,
  isReadOnly = false,
  existingCorrection,
  groupsCount,
  nonPreventableFields,
  applicant,
  formAssigment,
}: {
  formAssigment?: FormDetailResponse["form_assignments"][number];
  stepTitle: string;
  data: FormDetailResponse["steps"][number]["sections"];
  corrections: Map<string, { status: "DENIED" | "APPROVED"; reason?: string }>;
  setCorrections: React.Dispatch<
    React.SetStateAction<
      Map<string, { status: "DENIED" | "APPROVED"; reason?: string }>
    >
  >;
  currentGroupId?: string | null;
  lockedApprovedFields?: Set<string>;
  nonPreventableFields?: Set<string>;
  isReadOnly?: boolean;
  groupsCount: number;
  existingCorrection?: Correction;
  applicant?: {
    fullName: string;
    date?: string | null;
  };
}) => {
  const [tempField, setTempField] = useState<string | null>(null);
  const [modalState, setModalState] = useState<
    "idle" | "deny-reason" | "history"
  >("idle");
  const [readOnlyComments, setReadOnlyComments] = useState<Comment[]>([]);
  const [fieldSelected, setFieldSelected] = useState<Fields | null>(null);

  const onShowFieldHistory = useCallback(
    (field: Fields) => {
      setFieldSelected(field);
      setModalState("history");
    },
    [setFieldSelected],
  );

  const onCorrection = useCallback(
    (fieldId: string, status: "DENIED" | "APPROVED") => {
      if (isReadOnly) return; // No permitir cambios en modo solo lectura

      // Prevenir denegar campos que fueron aprobados en ciclos anteriores
      // PERO: cuando hay grupos (currentGroupId), no bloquear ningún campo
      if (
        status === "DENIED" &&
        !currentGroupId &&
        lockedApprovedFields?.has(fieldId)
      ) {
        return;
      }

      if (status === "DENIED") {
        const prevValue = corrections.get(fieldId);
        if (prevValue?.reason) {
          // If there's already a reason, just set the correction
          setCorrections((prev) => {
            const newMap = new Map(prev);
            newMap.set(fieldId, { status, reason: prevValue.reason });
            return newMap;
          });
          return;
        }

        setTempField(fieldId);
        setModalState("deny-reason");
        return;
      }
      setCorrections((prev) => {
        const newMap = new Map(prev);
        const prevValue = newMap.get(fieldId);
        // If the field was previously denied, keep the reason
        if (prevValue?.status === "DENIED") {
          newMap.set(fieldId, { status, reason: prevValue.reason });
          return newMap;
        }
        //if already accepted, do nothing
        if (prevValue?.status === "APPROVED") {
          return newMap;
        }
        // Otherwise, just set the status to accept
        newMap.set(fieldId, { status });
        return newMap;
      });
      setTempField(null);
      setModalState("idle");
    },
    [
      corrections,
      setCorrections,
      isReadOnly,
      lockedApprovedFields,
      currentGroupId,
    ],
  );

  const columns = useMemo(() => {
    return tableColumns({
      onCorrection,
      corrections,
      currentGroupId,
      lockedApprovedFields,
      onShowFieldHistory,
      onOpenCorrectionModal: (fieldId: string) => {
        // En modo solo lectura, buscar los comentarios existentes
        if (isReadOnly && existingCorrection) {
          const fieldCorrection = existingCorrection.field_corrections?.find(
            (fc) => fc.filled_field_id === fieldId,
          );
          if (fieldCorrection && fieldCorrection.comments) {
            // comments en field_corrections es un array de objetos Comment
            if (Array.isArray(fieldCorrection.comments)) {
              setReadOnlyComments(fieldCorrection.comments);
            }
          }
        }
        setTempField(fieldId);
        setModalState("deny-reason");
      },
      isReadOnly,
      existingCorrection,
      groupsCount,
      nonPreventableFields,
    });
  }, [
    nonPreventableFields,
    onCorrection,
    corrections,
    currentGroupId,
    lockedApprovedFields,
    isReadOnly,
    existingCorrection,
    groupsCount,
    onShowFieldHistory,
  ]);

  return (
    <>
      {modalState === "deny-reason" && (
        <DenyReasonModal
          isOpen={modalState === "deny-reason"}
          onClose={() => {
            setModalState("idle");
            setTempField(null);
            setReadOnlyComments([]);
          }}
          defaultReason={
            isReadOnly && readOnlyComments.length > 0
              ? readOnlyComments[0].text
              : tempField
                ? corrections.get(tempField)?.reason || ""
                : ""
          }
          onAccept={(reason) => {
            if (isReadOnly) {
              // En modo solo lectura, solo cerrar el modal
              setModalState("idle");
              setTempField(null);
              setReadOnlyComments([]);
              return;
            }

            if (tempField) {
              setCorrections((prev) => {
                const newMap = new Map(prev);
                newMap.set(tempField, {
                  status: "DENIED",
                  reason: reason.description,
                });
                return newMap;
              });
            }
            setModalState("idle");
            setTempField(null);
          }}
          readOnly={isReadOnly}
        />
      )}

      {modalState === "history" && fieldSelected && (
        <FieldHistoryModal
          isOpen={modalState === "history"}
          applicant={applicant}
          onClose={() => {
            setModalState("idle");
            setFieldSelected(null);
          }}
          formAssigment={formAssigment}
          fieldSelected={fieldSelected}
        />
      )}

      <div className="border-[0.5px] border-neutral-quiet rounded-2xl overflow-hidden w-full">
        <div className="p-3 bg-neutral-object border-b border-neutral-quiet text-body-loud rounded-t-2xl">
          <Heading variant="h6">{stepTitle}</Heading>
        </div>
        {data.map((section, index) => {
          // Filtrar campos condicionados que están vacíos
          const visibleFields = section.fields.filter((field) => {
            const isConditionalField = (field as { is_conditional?: boolean })
              .is_conditional;
            // Si es condicional y no tiene valores, no mostrarlo
            if (
              isConditionalField &&
              (!field.filled_fields || field.filled_fields.length === 0)
            ) {
              return false;
            }
            return true;
          });

          return (
            <div key={section.id}>
              <div className="py-1.5 px-3 text-body-loud font-semibold text-xs">
                <h6 className="uppercase">{section.title}</h6>
                {/* <Body size="xs">{section.description}</Body> */}
              </div>
              <TableData
                columns={columns}
                data={visibleFields}
                stripped
                emptyState={<span></span>}
                wrapperClassName={cn(
                  "[&_thead]:hidden overflow-x-hidden",
                  //"[&_thead]:hidden overflow-x-auto",
                  index === data.length - 1 && "rounded-b-2xl shadow-custom",
                )}
              />
            </div>
          );
        })}
      </div>
    </>
  );
};

const FormCorrection = () => {
  const navigate = useNavigate();
  const { isCancelled } = useInfo();
  const params = useParams<{ formId: string; procedureId: string }>();
  const [selectedRevisionId, setRevisionId] = useQueryState(
    "revision_id",
    parseAsString.withDefault(""),
  );
  const [selectedGroupId, setGroupId] = useQueryState(
    "group_id",
    parseAsString,
  );
  const { setShouldShowInfo } = useInfo();

  const { data, isPending, isLoading } = useGetFormDetail2(
    params.formId!,
    params.procedureId!,
  );
  console.log(data?.form_assignments);
  const { data: formalityRequest } = useGetFormalityRequestDetail(
    params.procedureId!,
  );

  // Get user profile to filter groups
  const { data: userProfile } = useGetMe();

  // Extract user group IDs
  const userGroupIds = useMemo(() => {
    return new Set(userProfile?.data?.groups?.map((group) => group.id) || []);
  }, [userProfile]);

  // Fetch revisions for the formality request using the correct endpoint
  const {
    data: revisionsRaw = [],
    isLoading: isLoadingRevisions,
    isFetching: isFetchingRevisions,
  } = useGetRevisions({
    relationships: [
      "creator",
      "groups",
      "applicantFormAssignment",
      "applicantFormAssignment.applicant",
      "corrections",
      "corrections.creator",
      "corrections.group",
      "corrections.comments",
      "corrections.comments.creator",
      "corrections.fieldCorrections",
      "corrections.fieldCorrections.comments",
      "corrections.fieldCorrections.comments.creator",
      "comments",
      "comments.creator",
      "deniedCorrections",
      "deniedCorrections.creator",
      "deniedCorrections.group",
      "deniedCorrections.comments",
      "deniedCorrections.comments.creator",
    ],
    filters: {
      formality_request_id: params.procedureId!,
    },
    enabled: !!params.procedureId,
  });

  // Filter revisions to show only groups that the user belongs to
  const revisions = useMemo(() => {
    return revisionsRaw.map((revision) => {
      const filteredGroups =
        revision.groups?.filter((group) => userGroupIds.has(group.id)) || [];
      const filteredCorrections =
        revision.corrections?.filter((correction) => {
          const groupId = correction.group?.id;
          return groupId && userGroupIds.has(groupId);
        }) || [];
      return {
        ...revision,
        groups: filteredGroups,
        corrections: filteredCorrections as Correction[],
      };
    });
  }, [revisionsRaw, userGroupIds]);

  // Compute pending VoBo counts for the leader view
  // A VoBo is pending when: revision.requires_leader_approval && correction.is_leader_approved === false && correction.status === 'IN_PROCESS'
  const { pendingVoBoMap, pendingVoBoCount } = useMemo(() => {
    const revisionIds = new Set<string>();
    const groupIds = new Set<string>();
    let count = 0;

    for (const revision of revisionsRaw) {
      if (!revision.requires_leader_approval) continue;

      for (const correction of revision.corrections ?? []) {
        if (
          correction.status === "IN_PROCESS" &&
          correction.is_leader_approved === false
        ) {
          revisionIds.add(revision.id);
          if (correction.group?.id) {
            groupIds.add(correction.group.id);
          }
          count++;
        }
      }
    }

    return {
      pendingVoBoMap: {
        revisions: revisionIds,
        groups: groupIds,
      } as PendingVoBoMap,
      pendingVoBoCount: count,
    };
  }, [revisionsRaw]);

  const [needsCorrectionCount, setNeedsCorrectionCount] = useState(0);

  const [corrections, setCorrections] = useState<
    Map<string, { status: "DENIED" | "APPROVED"; reason?: string }>
  >(new Map());

  // Set de IDs de campos que fueron aprobados en ciclos anteriores y no pueden ser denegados
  const [lockedApprovedFields, setLockedApprovedFields] = useState<Set<string>>(
    new Set(),
  );
  const [nonPreventableFields, setNonPreventableFields] = useState<Set<string>>(
    new Set(),
  );

  const [modalState, setModalState] = useState<"idle" | "general-reason">(
    "idle",
  );
  const [leaderModalState, setLeaderModalState] = useState<
    "idle" | "reject" | "approve"
  >("idle");
  const [rejectComments, setRejectComments] = useState("");
  const { showToast } = useToast();
  const { setFormDeadline } = useInfo();

  const revisionId = useMemo(() => {
    // Only use revision if explicitly selected from URL
    if (selectedRevisionId) return selectedRevisionId;

    // Don't auto-select any revision - let user choose
    return "";
  }, [selectedRevisionId]);

  useEffect(() => {
    if (data) {
      setShouldShowInfo(false);
      const { corr, lockedFields, count, nonPreventableFields } =
        getCorrections(data, revisionId, selectedGroupId);
      setCorrections(corr);
      setLockedApprovedFields(lockedFields);
      setNeedsCorrectionCount(count);
      setNonPreventableFields(nonPreventableFields);
      const exp = getInternalDeadlineFromFormData(data);
      setFormDeadline(exp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, revisionId, selectedGroupId, setShouldShowInfo]);

  const canSubmitCorrections = corrections.size === needsCorrectionCount;

  // Determinar si la revisión seleccionada es la última (más reciente)
  const isLatestRevision = useMemo(() => {
    if (!selectedRevisionId || revisions.length === 0) return true;

    // Ordenar revisiones por fecha de creación
    const sortedRevisions = [...revisions].sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return dateB - dateA; // Más reciente primero
    });

    // Verificar si la revisión seleccionada es la primera (más reciente)
    return sortedRevisions[0]?.id === selectedRevisionId;
  }, [selectedRevisionId, revisions]);

  // Verificar si el grupo seleccionado ya envió su corrección en el ciclo actual
  const groupCorrectionInCurrentCycle = useMemo(() => {
    if (!selectedRevisionId || !data) {
      return null;
    }

    // Usar data.form_assignments[0].revisions porque tiene current_cycle
    const revisionFromData = data?.form_assignments?.[0]?.revisions?.find(
      (r) => r.id === selectedRevisionId,
    );

    if (!revisionFromData) {
      return null;
    }

    const totalGroups =
      (revisionFromData as { groups?: Array<{ id: string; name: string }> })
        .groups?.length || 0;

    // const currentCycle =
    //   (revisionFromData as { current_cycle?: number }).current_cycle || 1;
    const corrections =
      (
        revisionFromData as {
          corrections?: Array<{
            id: string;
            group?: { id: string };
            group_id?: string;
            status: "APPROVED" | "DENIED" | "IN_PROCESS";
            comments?: Array<{ text: string }>;
          }>;
        }
      ).corrections || [];

    // Si hay grupos, buscar la corrección del grupo seleccionado
    if (totalGroups > 0 && selectedGroupId) {
      // Calcular el rango de correcciones para el ciclo actual
      // const startIdx = (currentCycle - 1) * totalGroups;
      // const endIdx = currentCycle * totalGroups;
      // console.log(startIdx, endIdx);
      // const currentCycleCorrections = corrections.slice(startIdx, endIdx);
      // console.log(currentCycleCorrections);
      // Buscar la corrección del grupo seleccionado
      const groupCorrection = corrections.find((correction) => {
        const groupId = correction?.group?.id || correction?.group_id;
        return (
          groupId === selectedGroupId && correction.status === "IN_PROCESS"
        );
      });

      // Enriquecer con datos completos de la revisión del API (que tiene creator)
      if (groupCorrection) {
        const revisionFromAPI = revisions.find(
          (r) => r.id === selectedRevisionId,
        );
        const fullCorrection = revisionFromAPI?.corrections?.find(
          (c) => c.id === groupCorrection.id,
        );

        return fullCorrection || groupCorrection;
      }

      return groupCorrection || null;
    }
    console.log(
      "No groups or no group selected, checking for any correction in current cycle",
    );

    // Si no hay grupos (o solo hay un grupo implícito), verificar si hay correcciones para esta revisión
    // Esto maneja el caso cuando hay correcciones pero no hay múltiples grupos
    if (corrections.length > 0) {
      // Si hay correcciones para esta revisión, retornar la más reciente
      // Esto hace que se muestre la vista de solo lectura cuando ya se enviaron observaciones
      const lastCorrection = corrections[0];

      // Enriquecer con datos completos de la revisión del API (que tiene creator)
      if (lastCorrection) {
        const revisionFromAPI = revisions.find(
          (r) => r.id === selectedRevisionId,
        );
        const fullCorrection = revisionFromAPI?.corrections?.find(
          (c) => c.id === lastCorrection.id,
        );
        return fullCorrection || lastCorrection;
      }

      return lastCorrection || null;
    }

    return null;
  }, [selectedRevisionId, selectedGroupId, data, revisions]);

  console.log(groupCorrectionInCurrentCycle);

  // Determinar si se debe mostrar la vista de solo lectura
  const isReadOnlyView = groupCorrectionInCurrentCycle !== null;

  // Obtener la revisión seleccionada para verificar requires_leader_approval
  const selectedRevision = useMemo(() => {
    if (!selectedRevisionId) return null;
    return revisions.find((rev) => rev.id === selectedRevisionId) || null;
  }, [selectedRevisionId, revisions]);

  const groupsCount = useMemo(() => {
    if (!selectedRevision) return 0;
    return selectedRevision.groups?.length || 0;
  }, [selectedRevision]);

  // Determinar si mostrar "Enviado al líder"
  const showSentToLeaderBadge = useMemo(() => {
    if (!selectedRevision || !groupCorrectionInCurrentCycle) return false;
    console.log(groupCorrectionInCurrentCycle);
    return (
      selectedRevision.requires_leader_approval === true &&
      (groupCorrectionInCurrentCycle as Correction).is_leader_approved === false
    );
  }, [selectedRevision, groupCorrectionInCurrentCycle]);
  console.log(showSentToLeaderBadge);

  // Verificar si el usuario es líder del grupo seleccionado
  const isUserLeaderOfSelectedGroup = useMemo(() => {
    if (!selectedGroupId || !userProfile?.data?.groups) return false;
    const group = userProfile.data.groups.find((g) => g.id === selectedGroupId);
    return group?.pivot?.seniority === 1 || group?.pivot?.seniority === 2;
  }, [selectedGroupId, userProfile]);
  console.log(isUserLeaderOfSelectedGroup);

  const isUserLeaderOfCorrectionGroup = useMemo(() => {
    if (!userProfile?.data?.groups || !groupCorrectionInCurrentCycle)
      return false;

    const correctionGroupId =
      (groupCorrectionInCurrentCycle as Correction).group?.id ||
      selectedGroupId;

    if (!correctionGroupId) return false;

    const group = userProfile.data.groups.find(
      (g) => g.id === correctionGroupId,
    );
    return group?.pivot?.seniority === 1;
  }, [userProfile, groupCorrectionInCurrentCycle, selectedGroupId]);

  const { mutateAsync: updateRevision, isPending: isLoadingUpdateRevision } =
    useUpdateRevision();

  const { mutateAsync: leaderApproval, isPending: isLoadingLeaderApproval } =
    useLeaderApproval();

  const handleLeaderReject = async () => {
    if (!groupCorrectionInCurrentCycle || !rejectComments.trim()) return;

    try {
      await leaderApproval({
        correction_id: groupCorrectionInCurrentCycle.id,
        approved: false,
        comments: rejectComments,
      });

      showToast({
        title: "Comentarios rechazados",
        description: "Los comentarios han sido rechazados y eliminados",
        options: {
          type: "error",
        },
      });

      setLeaderModalState("idle");
      setRejectComments("");
      navigate(`/tramite/${params.procedureId}`);
    } catch {
      showToast({
        title: "Error al rechazar",
        description: "Ocurrió un error al rechazar los comentarios",
        options: {
          type: "error",
        },
      });
    }
  };

  const handleLeaderApprove = async () => {
    if (!groupCorrectionInCurrentCycle) return;

    try {
      await leaderApproval({
        correction_id: groupCorrectionInCurrentCycle.id,
        approved: true,
      });

      showToast({
        title: "Comentarios aprobados",
        description: `Estos comentarios han sido enviados enviados al usuario correspondiente`,
        options: {
          type: "success",
        },
      });

      setLeaderModalState("idle");
      navigate(`/tramite/${params.procedureId}`);
    } catch {
      showToast({
        title: "Error al aprobar",
        description: "Ocurrió un error al aprobar los comentarios",
        options: {
          type: "error",
        },
      });
    }
  };

  const onSubmitCorrections = () => {
    if (!data) return;

    // Check if there are any denied fields
    const hasDeniedFields = Array.from(corrections.values()).some(
      (correction) => correction.status === "DENIED",
    );
    if (hasDeniedFields) {
      setModalState("general-reason");
    } else {
      handleSubmitWithGeneralComment();
    }
  };

  const handleSubmitWithGeneralComment = async (generalReason?: string) => {
    if (!data) return;
    const filledFieldCorrections = Array.from(corrections.entries())
      .filter(([fieldIdentifier]) => {
        // Solo incluir correcciones para campos que tienen filled_field_id (no IDs sintéticos como "field_123")
        return !fieldIdentifier.startsWith("field_");
      })
      .map(([filled_field_id, { status, reason }]) => ({
        filled_field_id,
        status,
        ...(status === "DENIED" && reason ? { comment: reason } : {}),
      }));
    try {
      // Verificar que form_assignments existe y tiene elementos
      if (!data?.form_assignments?.[0]?.revisions?.length) {
        throw new Error("No hay revisiones disponibles");
      }

      const revisionCounts = data.form_assignments[0].revisions.length;
      const currentRevisionId =
        selectedRevisionId ||
        data.form_assignments[0].revisions[revisionCounts - 1]?.id;

      if (!currentRevisionId) {
        throw new Error("No se pudo encontrar el ID de revisión");
      }

      const activeRevision =
        data.form_assignments[0].revisions.find(
          (rev) => rev.id === currentRevisionId,
        ) || data.form_assignments[0].revisions[revisionCounts - 1];

      if (!activeRevision) {
        throw new Error("No se encontró la revisión activa");
      }

      const revisionGroups = (
        activeRevision as { groups?: Array<{ id: string }> }
      ).groups;

      // Use selected group from URL if available, otherwise fall back to existing logic
      const groupId =
        selectedGroupId ||
        (revisionGroups && revisionGroups.length > 0
          ? revisionGroups[revisionGroups.length - 1]?.id
          : null) ||
        (activeRevision as { approval_group_id?: string }).approval_group_id ||
        (activeRevision as { group_id?: string }).group_id ||
        (data.form_assignments[0] as { approval_group_id?: string })
          .approval_group_id ||
        (data.form_assignments[0] as { group_id?: string }).group_id ||
        formalityRequest?.groups?.[formalityRequest.groups.length - 1]?.id ||
        formalityRequest?.group_id;

      await updateRevision({
        revision_id: currentRevisionId,
        // correction_fields can be empty when revision has groups
        correction_fields: filledFieldCorrections,
        correction_comment:
          generalReason?.trim() || "No se agregaron comentarios adicionales.",
        ...(groupId ? { group_id: groupId } : {}),
      });
      setModalState("idle");

      // Mostrar toast diferente si requiere aprobación de líder y el usuario no es líder
      const requiresLeaderApproval =
        selectedRevision?.requires_leader_approval ?? false;

      if (requiresLeaderApproval && !isUserLeaderOfSelectedGroup) {
        showToast({
          title: "Observaciones enviadas a aprobación",
          description:
            "Las observaciones realizadas en este formulario han sido enviadas a tu líder para poder ser aprobadas.",
          options: {
            type: "success",
          },
        });
      } else {
        showToast({
          title: "Correcciones enviadas",
          description: "Las correciones se enviaron exitosamente",
          options: {
            type: "success",
          },
        });
      }
      navigate(`/tramite/${params.procedureId}`);
    } catch (e) {
      const error = e as AxiosError<{ message: string }>;
      // Handle 422 status code with specific message
      if (error?.response?.status === 422 && error?.response?.data?.message) {
        showToast({
          title: "Error de validación",
          description: error.response.data.message,
          options: {
            type: "error",
          },
        });
      } else {
        showToast({
          title: "Error al enviar correcciones",
          description:
            "Ocurrió un error al enviar las correcciones, por favor intenta nuevamente",
          options: {
            type: "error",
          },
        });
      }
    }
  };

  useEffect(() => {
    return () => {
      setCorrections(new Map());
    };
  }, []);

  const handleRevisionSelect = (revisionId: string, groupId?: string) => {
    setRevisionId(revisionId);
    setGroupId(groupId || null);
  };

  const shouldDisabledDropdown = useMemo(() => {
    console.log(revisions.length);
    //should be disabled if there are no revisions or if there is only one revision without groups
    if (revisions.length === 0) return true;
    if (revisions.length === 1) {
      const rev = revisions[0];
      return !rev.groups || rev.groups.length === 1 || rev.groups.length === 0;
    }
    return false;
  }, [revisions]);

  if (isPending || isLoading || !data || isLoadingRevisions) {
    return (
      <div>
        <Icon
          name="spinner-third"
          type="fas"
          className="text-on-disabled animate-spin"
        />
      </div>
    );
  }

  return (
    <>
      {/* Header with Revision Selector */}
      <div
        className={cn(
          "flex items-center gap-3 px-4 pb-4",
          !shouldDisabledDropdown && "border-b border-neutral-quiet",
        )}
      >
        <RevisionSelector
          revisions={revisions}
          selectedRevisionId={selectedRevisionId || undefined}
          selectedGroupId={selectedGroupId || undefined}
          onSelect={handleRevisionSelect}
          pendingVoBo={pendingVoBoMap}
          shouldDisabledDropdown={shouldDisabledDropdown}
        />
        {pendingVoBoCount > 0 && isUserLeaderOfSelectedGroup && (
          <Badge
            variant="warning"
            className="flex items-center gap-1.5 px-3 py-1 shrink-0"
          >
            <Icon type="fas" name="arrow-left" className="text-xs" />
            Aprobaciones pendientes: {pendingVoBoCount}
          </Badge>
        )}
      </div>

      {/* Form content */}
      {!selectedRevisionId ? (
        // Mostrar mensaje para seleccionar una revisión
        <div className="flex flex-col items-center justify-center gap-4 px-4 pt-20">
          <Icon name="file-edit" type="fas" className="text-white text-5xl" />
          <div className="text-center">
            <Heading variant="h6" className="text-neutral-loud">
              Selecciona una revisión
            </Heading>
            <p className="text-body text-sm mt-2">
              Utiliza el selector de arriba para elegir la revisión que deseas
              revisar
            </p>
          </div>
        </div>
      ) : isReadOnlyView ? (
        // Vista de solo lectura - Mostrar corrección existente
        <div className="flex flex-col gap-8 px-4 pt-4">
          {/* Header del formulario */}
          <div className="flex justify-between items-center">
            <div>
              <Heading variant="h6">{data.name}</Heading>
              <p className="font-body text-body text-size-body">
                {data.description || "Revisa las respuestas del formulario."}
              </p>
            </div>
            <Badge variant="warning" className="h-5.5">
              {showSentToLeaderBadge
                ? "Pendiente de revisión de líder"
                : "Observado"}
              <Icon
                type="fas"
                name="file-edit"
                className="text-warning-quiet"
              />
            </Badge>
          </div>

          {/* Información del analista que realizó la revisión (visible para el líder) */}
          {isUserLeaderOfSelectedGroup && groupCorrectionInCurrentCycle && (
            <div className="bg-neutral-object border border-neutral-quiet rounded-lg p-3 flex items-center gap-3">
              <Icon type="far" name="user" className="text-body text-base" />
              <div className="flex flex-col">
                <span className="text-body-loud text-sm font-semibold">
                  {(() => {
                    const correction =
                      groupCorrectionInCurrentCycle as Correction;
                    const creatorFromComments =
                      correction?.comments?.[0]?.creator;
                    const creator = creatorFromComments || correction?.creator;
                    if (
                      creator &&
                      typeof creator === "object" &&
                      "name" in creator
                    ) {
                      return `${creator.name || ""} ${creator.last_name || ""}`.trim();
                    }
                    return "Analista";
                  })()}
                </span>
                {(groupCorrectionInCurrentCycle as Correction)?.group && (
                  <span className="text-body text-xs">
                    Grupo:{" "}
                    {(groupCorrectionInCurrentCycle as Correction).group?.name}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Comentarios del líder (deniedCorrections) - visible solo para analistas, no para el líder */}
          {isFetchingRevisions ? (
            <Skeleton className="w-full h-16 rounded-lg" />
          ) : (
            !isUserLeaderOfSelectedGroup &&
            selectedRevision?.denied_corrections &&
            selectedRevision.denied_corrections.length > 0 && (
              <div className="flex flex-col gap-2">
                {selectedRevision.denied_corrections
                  .filter((dc: DeniedCorrection) => {
                    // Filtrar por grupo seleccionado si aplica
                    if (selectedGroupId && dc.group?.id) {
                      return dc.group.id === selectedGroupId;
                    }
                    return true;
                  })
                  .filter((dc: DeniedCorrection) => {
                    // No mostrar si el analista ya envió una corrección más reciente que el rechazo del líder
                    const leaderComment =
                      dc.comments.length > 0
                        ? dc.comments[dc.comments.length - 1]
                        : null;
                    if (!leaderComment?.created_at) return true;
                    const leaderRejectTime = new Date(
                      leaderComment.created_at,
                    ).getTime();
                    const groupId = dc.group?.id;
                    // Verificar si hay una corrección posterior al rechazo del líder para el mismo grupo
                    const hasNewerCorrection = (
                      selectedRevision.corrections ?? []
                    ).some((c) => {
                      const corrGroupId = c.group?.id;
                      const matchesGroup = groupId
                        ? corrGroupId === groupId
                        : true;
                      const corrTime = new Date(c.created_at ?? 0).getTime();
                      return matchesGroup && corrTime > leaderRejectTime;
                    });
                    return !hasNewerCorrection;
                  })
                  .map((dc: DeniedCorrection) => {
                    // El último mensaje de la corrección denegada siempre es del líder
                    const leaderComment =
                      dc.comments.length > 0
                        ? dc.comments[dc.comments.length - 1]
                        : null;
                    if (!leaderComment?.text) return null;
                    return (
                      <div
                        key={dc.id}
                        className="bg-surface-danger-accent-quiet border rounded-lg border-contextual-danger-quiet p-2 flex flex-col gap-1"
                      >
                        <div className="flex items-center gap-2">
                          <h1 className="text-h1 text-heading text-size-body-sm leading-2xl">
                            Comentario del líder
                          </h1>
                          {leaderComment.creator &&
                            typeof leaderComment.creator === "object" &&
                            "name" in leaderComment.creator && (
                              <span className="text-body text-xs">
                                —{" "}
                                {(
                                  leaderComment.creator as {
                                    name?: string;
                                    last_name?: string;
                                  }
                                ).name || ""}{" "}
                                {(
                                  leaderComment.creator as {
                                    name?: string;
                                    last_name?: string;
                                  }
                                ).last_name || ""}
                              </span>
                            )}
                        </div>
                        <p className="font-body text-body text-size-body whitespace-pre-wrap">
                          {stripHtmlTags(leaderComment.text)}
                        </p>
                      </div>
                    );
                  })}
              </div>
            )
          )}

          {/* Comentario general si existe */}
          {isFetchingRevisions ? (
            <Skeleton className="w-full h-16 rounded-lg" />
          ) : (
            groupCorrectionInCurrentCycle?.comments &&
            groupCorrectionInCurrentCycle.comments.length > 0 &&
            groupCorrectionInCurrentCycle.comments[0]?.text && (
              <div className="bg-surface-warning-accent-quiet border rounded-lg border-contextual-warning-quiet p-2 flex flex-col gap-1">
                <h1 className="text-h1 text-heading text-size-body-sm leading-2xl">
                  Comentario general
                </h1>
                <p className="font-body text-body text-size-body whitespace-pre-wrap">
                  {stripHtmlTags(
                    groupCorrectionInCurrentCycle.comments[0].text,
                  )}
                </p>
              </div>
            )
          )}

          {/* Formulario con campos en modo solo lectura */}
          <div className="flex flex-col gap-6">
            {data.steps
              .filter((step) => {
                return step.sections.some((section) => {
                  return section.fields.some((field) => {
                    // Verificar si el campo tiene filled_fields y si no está vacío
                    if (field.filled_fields && field.filled_fields.length > 0) {
                      return true;
                    }
                    // Si no tiene filled_fields, mostrarlo por defecto
                    return !field.filled_fields?.length;
                  });
                });
              })
              .map((step) => (
                <SectionList
                  key={step.id}
                  formAssigment={
                    data?.form_assignments?.[data?.form_assignments?.length - 1]
                  }
                  stepTitle={step.name}
                  corrections={corrections}
                  setCorrections={setCorrections}
                  data={step.sections.filter((section) => {
                    console.log(section.name);
                    return section.fields.some((field) => {
                      if (
                        field.filled_fields &&
                        field.filled_fields.length > 0
                      ) {
                        return true;
                      }
                      return !field.filled_fields?.length;
                    });
                  })}
                  currentGroupId={selectedGroupId}
                  lockedApprovedFields={lockedApprovedFields}
                  isReadOnly={true}
                  existingCorrection={
                    groupCorrectionInCurrentCycle as Correction
                  }
                  groupsCount={groupsCount}
                  nonPreventableFields={nonPreventableFields}
                  applicant={{
                    fullName: `${formalityRequest?.applicant?.first_name} ${formalityRequest?.applicant?.last_name}`,
                    date:
                      formalityRequest?.updated_at ||
                      formalityRequest?.created_at,
                  }}
                />
              ))}
          </div>

          {/* Botones de líder: Rechazar y Aprobar */}
          {isUserLeaderOfCorrectionGroup && showSentToLeaderBadge && (
            <div className="flex gap-3 justify-end mt-4 sticky bottom-0 bg-surface-default py-4 border-t border-neutral-quiet">
              <Button
                variant="danger-secondary"
                onClick={() => setLeaderModalState("reject")}
                disabled={isLoadingLeaderApproval}
              >
                Rechazar
              </Button>
              <Button
                variant="default"
                onClick={() => setLeaderModalState("approve")}
                disabled={isLoadingLeaderApproval}
              >
                Aprobar
              </Button>
            </div>
          )}
        </div>
      ) : (
        // Vista normal - Permitir hacer correcciones
        <div className="flex flex-col gap-6 w-full px-4 pt-4">
          {/* Comentarios del líder (deniedCorrections) - visible para el analista al re-hacer correcciones */}
          {isFetchingRevisions ? (
            <Skeleton className="w-full h-16 rounded-lg" />
          ) : (
            selectedRevision?.denied_corrections &&
            selectedRevision.denied_corrections.length > 0 && (
              <div className="flex flex-col gap-2">
                {selectedRevision.denied_corrections
                  .filter((dc: DeniedCorrection) => {
                    if (selectedGroupId && dc.group?.id) {
                      return dc.group.id === selectedGroupId;
                    }
                    return true;
                  })
                  .filter((dc: DeniedCorrection) => {
                    // No mostrar si el analista ya envió una corrección más reciente que el rechazo del líder
                    const leaderComment =
                      dc.comments.length > 0
                        ? dc.comments[dc.comments.length - 1]
                        : null;
                    if (!leaderComment?.created_at) return true;
                    const leaderRejectTime = new Date(
                      leaderComment.created_at,
                    ).getTime();
                    const groupId = dc.group?.id;
                    const hasNewerCorrection = (
                      selectedRevision.corrections ?? []
                    ).some((c) => {
                      const corrGroupId = c.group?.id;
                      const matchesGroup = groupId
                        ? corrGroupId === groupId
                        : true;
                      const corrTime = new Date(c.created_at ?? 0).getTime();
                      return matchesGroup && corrTime > leaderRejectTime;
                    });
                    return !hasNewerCorrection;
                  })
                  .map((dc: DeniedCorrection) => {
                    const leaderComment =
                      dc.comments.length > 0
                        ? dc.comments[dc.comments.length - 1]
                        : null;
                    if (!leaderComment?.text) return null;
                    return (
                      <div
                        key={dc.id}
                        className="bg-surface-danger-accent-quiet border rounded-lg border-contextual-danger-quiet p-2 flex flex-col gap-1"
                      >
                        <div className="flex items-center gap-2">
                          <h1 className="text-h1 text-heading text-size-body-sm leading-2xl">
                            Comentario del líder
                          </h1>
                          {leaderComment.creator &&
                            typeof leaderComment.creator === "object" &&
                            "name" in leaderComment.creator && (
                              <span className="text-body text-xs">
                                —{" "}
                                {(
                                  leaderComment.creator as {
                                    name?: string;
                                    last_name?: string;
                                  }
                                ).name || ""}{" "}
                                {(
                                  leaderComment.creator as {
                                    name?: string;
                                    last_name?: string;
                                  }
                                ).last_name || ""}
                              </span>
                            )}
                        </div>
                        <p className="font-body text-body text-size-body whitespace-pre-wrap">
                          {stripHtmlTags(leaderComment.text)}
                        </p>
                      </div>
                    );
                  })}
              </div>
            )
          )}
          {data.steps
            .filter((step) => {
              return step.sections.some((section) => {
                return section.fields.some((field) => {
                  // Verificar si el campo tiene filled_fields y si no está vacío
                  if (field.filled_fields && field.filled_fields.length > 0) {
                    return true;
                  }
                  // Si no tiene filled_fields, mostrarlo por defecto
                  return false;
                });
              });
            })
            .map((step) => (
              <SectionList
                key={step.id}
                formAssigment={
                  data?.form_assignments?.[data?.form_assignments?.length - 1]
                }
                stepTitle={step.name}
                corrections={corrections}
                setCorrections={setCorrections}
                data={step.sections.filter((section) => {
                  return section.fields.some((field) => {
                    if (
                      section.title ===
                      "Datos del envase y presentación del producto"
                    ) {
                      console.log(field.filled_fields);
                    }
                    if (field.filled_fields && field.filled_fields.length > 0) {
                      return true;
                    }
                    return false;
                  });
                })}
                currentGroupId={selectedGroupId}
                lockedApprovedFields={lockedApprovedFields}
                isReadOnly={isCancelled}
                groupsCount={groupsCount}
                nonPreventableFields={nonPreventableFields}
                applicant={{
                  fullName: `${formalityRequest?.applicant?.first_name} ${formalityRequest?.applicant?.last_name}`,
                  date:
                    formalityRequest?.updated_at ||
                    formalityRequest?.created_at,
                }}
              />
            ))}
          <div className="flex flex-col gap-2 items-end">
            {!isLatestRevision && selectedRevisionId && (
              <p className="text-warning text-sm">
                No puedes enviar correcciones de una revisión anterior.
                Selecciona la revisión más reciente.
              </p>
            )}
            <Button
              type="button"
              disabled={
                !canSubmitCorrections || !isLatestRevision || isCancelled
              }
              onClick={onSubmitCorrections}
              isLoading={isLoadingUpdateRevision && modalState === "idle"}
            >
              Enviar
            </Button>
          </div>
          {modalState === "general-reason" && (
            <DenyGeneralReasonModal
              isOpen={modalState === "general-reason"}
              onClose={() => {
                setModalState("idle");
              }}
              isLoading={isLoadingUpdateRevision}
              onAccept={(val) => {
                handleSubmitWithGeneralComment(val.description);
              }}
            />
          )}
        </div>
      )}

      {/* Modal de Rechazo del Líder */}
      <Dialog
        open={leaderModalState === "reject"}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setLeaderModalState("idle");
            setRejectComments("");
          }
        }}
      >
        <DialogContent className="sm:max-w-156 gap-6">
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-3xl font-semibold text-heading text-center">
              Rechazar comentarios
            </DialogTitle>
            <DialogDescription className="text-base text-body leading-6 text-center">
              ¿Estás seguro de que deseas rechazar la revisión por{" "}
              <span className="text-action-loud font-semibold">
                {(() => {
                  // Creator puede estar en comments[0].creator o field_corrections[0].comments[0].creator
                  const correction =
                    groupCorrectionInCurrentCycle as Correction;
                  const creatorFromComments =
                    correction?.comments?.[0]?.creator;
                  const creator = creatorFromComments || correction?.creator;

                  const fullName =
                    creator && typeof creator === "object" && "name" in creator
                      ? `${creator.name || ""} ${creator.last_name || ""}`.trim()
                      : "nombre de técnico";

                  return fullName;
                })()}
              </span>
              ? La revisión en esta vuelta de revisión será eliminada y marcada
              como revisión pendiente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-semibold text-heading block">
              Motivo del rechazo<span className="text-danger-loud">*</span>
            </label>
            <Textarea
              value={rejectComments}
              onChange={(e) => setRejectComments(e.target.value)}
              placeholder="Escribe el motivo del rechazo..."
              rows={4}
              className="w-full resize-none"
            />
          </div>
          <DialogFooter className="flex flex-row gap-3 sm:flex-row">
            <Button
              variant="quiet"
              onClick={() => {
                setLeaderModalState("idle");
                setRejectComments("");
              }}
              disabled={isLoadingLeaderApproval}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="danger-primary"
              onClick={handleLeaderReject}
              disabled={!rejectComments.trim() || isLoadingLeaderApproval}
              className="flex-1"
            >
              {isLoadingLeaderApproval ? "Rechazando..." : "Rechazar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Aprobación del Líder */}
      <Dialog
        open={leaderModalState === "approve"}
        onOpenChange={(open: boolean) => {
          if (!open) setLeaderModalState("idle");
        }}
      >
        <DialogContent className="sm:max-w-156 gap-6">
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-3xl font-semibold text-heading text-center">
              Aprobar comentarios
            </DialogTitle>
            <DialogDescription className="text-base text-body leading-6 text-center">
              ¿Estás seguro de que deseas aprobar comentarios realizados por{" "}
              <span className="text-action-loud font-semibold">
                {(() => {
                  // Creator puede estar en comments[0].creator o field_corrections[0].comments[0].creator
                  const correction =
                    groupCorrectionInCurrentCycle as Correction;
                  const creatorFromComments =
                    correction?.comments?.[0]?.creator;
                  const creator = creatorFromComments || correction?.creator;

                  const fullName =
                    creator && typeof creator === "object" && "name" in creator
                      ? `${creator.name || ""} ${creator.last_name || ""}`.trim()
                      : "nombre de técnico";

                  return fullName;
                })()}
              </span>
              ? Estos comentarios serán enviados a{" "}
              <span className="text-action-loud font-semibold">
                {(() => {
                  const revisionWithApplicant = revisionsRaw.find(
                    (r) => r.id === selectedRevisionId,
                  );
                  const applicant =
                    revisionWithApplicant?.applicant_form_assignment?.applicant;

                  if (
                    applicant &&
                    typeof applicant === "object" &&
                    "first_name" in applicant
                  ) {
                    return `${applicant.first_name || ""} ${applicant.last_name || ""}`.trim();
                  }
                  return "nombre de usuario";
                })()}
              </span>{" "}
              en la solicitud{" "}
              <span className="text-action-loud font-semibold">
                {data?.name || formalityRequest?.name || "nombre de solicitud"}
              </span>
              .
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-row gap-3 sm:flex-row pt-2">
            <Button
              variant="quiet"
              onClick={() => setLeaderModalState("idle")}
              disabled={isLoadingLeaderApproval}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="default"
              onClick={handleLeaderApprove}
              disabled={isLoadingLeaderApproval}
              className="flex-1"
            >
              {isLoadingLeaderApproval ? "Aprobando..." : "Aprobar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FormCorrection;

// data.steps.forEach((step) => {
//   step.sections.forEach((section) => {
//     section.fields.forEach(({ filled_fields }) => {
//       //filter filled_fields that has correction.correction.revision_id equal to revisionId
//       const relevantFilledFields = filled_fields?.filter(
//         (ff) =>
//           ff.correction && ff.correction.correction.revision_id === revisionId,
//       );
//       if (relevantFilledFields && relevantFilledFields.length > 0) {
//         const latestFilledField =
//           relevantFilledFields[relevantFilledFields.length - 1];
//         const correction = latestFilledField.correction;

//         const hasSomeApproved = relevantFilledFields.some(({ correction }) => {
//           if (!correction) {
//             return false;
//           }
//           return correction.status === "APPROVED";
//         });
//         console.log(correction);
//         if (!correction && !hasSomeApproved) {
//           count += 1;
//         }
//       }
//     });
//   });
// });
